// Cloudflare Turnstile widget + shared honeypot input (IH-RAPP-001 slice 4).
// Key-independent: no VITE_TURNSTILE_SITE_KEY -> the widget renders NOTHING
// and forms submit exactly as before (the backend gate is off until the
// operator sets its secret — both keys deploy together, backend first is
// forbidden: see .env.example's deploy-order warning). The challenge script
// is Cloudflare's own and cannot be self-hosted — same precedent as the
// Google GSI script on /sign-in; it is only injected when a key exists.
import { useEffect, useRef } from 'react';

export const TURNSTILE_SITE_KEY =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || '';

declare global {
  interface Window { turnstile?: any }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => { scriptPromise = null; reject(new Error('turnstile script failed')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

// Renders the challenge and reports tokens up; empty string on expiry/error
// so a stale token is never submitted.
export function TurnstileWidget({ onToken }: { onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let widgetId: string | undefined;
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !ref.current || !window.turnstile) return;
      widgetId = window.turnstile.render(ref.current, {
        sitekey: TURNSTILE_SITE_KEY,
        // pinned light + flexible: the app hard-locks light mode (main.tsx),
        // so 'auto' would hand dark-OS visitors a dark card on sage paper —
        // same convention as the GSI button's theme pin; flexible keeps the
        // fixed-width iframe inside narrow mobile columns.
        theme: 'light',
        size: 'flexible',
        callback: (t: string) => onTokenRef.current(t),
        'expired-callback': () => onTokenRef.current(''),
        'error-callback': () => onTokenRef.current(''),
      });
    }).catch(() => { /* script blocked: backend fail-closed still governs */ });
    return () => {
      cancelled = true;
      if (widgetId !== undefined && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, []);

  if (!TURNSTILE_SITE_KEY) return null;
  // no margin of its own — the host form's rhythm governs spacing
  return <div ref={ref} />;
}

// The shared hidden honeypot ("website" — the field name the backend checks).
// data-1p-ignore / data-lpignore keep password managers from auto-filling it:
// a filled honeypot on an auth form is an unexplained permanent 400.
export function Honeypot({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="text" name="website" value={value}
      onChange={e => onChange(e.target.value)}
      tabIndex={-1} autoComplete="off" aria-hidden="true"
      data-1p-ignore="true" data-lpignore="true"
      style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }}
    />
  );
}
